"use client"

import Text from "@/components/common/Typography/Text"
import { Card, Col, Divider, Popover, Row } from "antd"
import { createContext, memo, ReactNode, useEffect, useRef, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChevronDown } from "@fortawesome/free-solid-svg-icons"

import { TypeAirport } from "@/lib/redux/features/flight/type"
import InputSearch, { InputSearchRef } from "./InputSearch"
import FlightPopularDestinationTabs from "./FlightPopularDestinationTabs"
import RecentlySearch from "./RecentlySearch"
import SearchingResults from "./SearchingResults"
import { useTETranslation } from "@/hooks/app/useTETranslation"
import { useFlightSearchPopoverMaxWidth } from "./FlightSearchPopoverBoundary"
import { useAppSelector } from "@/lib/redux/hooks"
import {
  closeAllFlightSearchOverlays,
  registerFlightSearchOverlayCloser,
} from "../../flightSearchOverlays"

type SelectPlacementProps = {
  label: string,
  icon: ReactNode,
  placeholder: string,

  value?: TypeAirport,
  onSearch?: (searchValue?: string) => void,
  onChange?: (airport?: TypeAirport) => void,
  chooseCurrentLy?: (from: TypeAirport, to: TypeAirport) => void,
  size?: 'small' | 'default',
  layout?: 'default' | 'hero',
  searching?: boolean,
  isPopup?: boolean,
  getRefInput?: (current: InputSearchRef | null) => void,
  onlyContent?: boolean
  /** bottomLeft for departure; bottomRight for destination so the panel stays inside the from+to strip */
  popoverPlacement?: 'bottomLeft' | 'bottomRight'
}

type TypeSelectPlacementContext = {
  onChange?: (airport?: TypeAirport) => void,
  chooseCurrentLy?: (from: TypeAirport, to: TypeAirport) => void,
  searching?: boolean,
  size?: 'small' | 'default',
  onSearch?: (searchValue?: string) => void,
  value?: TypeAirport,
  open?: () => void
}

export const SelectPlacementContext = createContext<TypeSelectPlacementContext>({

})

/** Thu hẹp popover hai bên so với khung điểm đi/đến  |-> <-| (giữa 2 khung điểm đi/đến) */
const FLIGHT_POPOVER_WIDTH_INSET_PX = 50
const HERO_POPOVER_MIN_WIDTH_PX = 420

// SELECT PLACEMENTS COMPONENTS
const SelectPlacement = ({ getRefInput, isPopup = true, size = 'default', layout = 'default', searching, value, onlyContent, onChange, onSearch, chooseCurrentLy, popoverPlacement = 'bottomLeft', ...props }: SelectPlacementProps) => {
  const isHero = layout === 'hero'
  const popoverMaxWidthPx = useFlightSearchPopoverMaxWidth()
  const popoverWidthPx =
    popoverMaxWidthPx != null
      ? Math.max(isHero ? HERO_POPOVER_MIN_WIDTH_PX : 260, popoverMaxWidthPx - FLIGHT_POPOVER_WIDTH_INSET_PX)
      : isHero
        ? HERO_POPOVER_MIN_WIDTH_PX
        : undefined
  const ref = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState<boolean>(false)
  const refInputSearch = useRef<InputSearchRef>(null)
  const { getAirportCityName, getTranslatedText } = useTETranslation()
  const currentlyFlights = useAppSelector(state => state.flight.currentlyFlights)
  const searchPopular = useAppSelector(state => state.flight.searchPopular)
  const hasRecentVisible = Object.keys(currentlyFlights || {}).length > 0 && !(searching && !searchPopular)

  const isSmall = size === 'small'
  const refContent = useRef<HTMLDivElement>(null)
  const clearingRef = useRef(false)

  const handleChange = (airport?: TypeAirport) => {
    onChange && onChange(airport)
    setOpen(false)
  }

  useEffect(() => {
    return registerFlightSearchOverlayCloser(() => setOpen(false))
  }, [])

  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node
      if (
        ref.current?.contains(target) ||
        refInputSearch.current?.contains(target) ||
        refContent.current?.contains(target)
      ) {
        return
      }
      setOpen(false)
    }

    document.addEventListener("pointerdown", handlePointerDown)
    return () => document.removeEventListener("pointerdown", handlePointerDown)
  }, [])

  useEffect(() => {
    if (!refInputSearch.current) return
    if (!value) {
      refInputSearch.current.setSearchValue("")
      return
    }
    refInputSearch.current.setSearchValue(getAirportCityName(value, { shownCode: true }))
  }, [value, isSmall])

  const content = (
    <>
      <FlightPopularDestinationTabs />
      <SearchingResults />
    </>
  )
  const handleClearAirport = () => {
    // Clear must not reopen this popover or jump to date / other airport field
    clearingRef.current = true
    onChange?.(undefined)
    refInputSearch.current?.setSearchValue("")
    setOpen(false)
    window.setTimeout(() => {
      clearingRef.current = false
    }, 0)
  }

  const openPopover = () => {
    if (clearingRef.current) return
    closeAllFlightSearchOverlays()
    setOpen(true)
  }

  let contentInput = isHero ? (
    <div className="home__searching--field-control home__searching--field-control--stacked">
      <Text className="home__searching--field-label">{props.label}</Text>
      <div className="home__searching--field-control-row">
        <span className="home__searching--field-icon" aria-hidden>
          {props.icon}
        </span>
        <InputSearch
          ref={(curr) => {
            refInputSearch.current = curr
            if (getRefInput) {
              getRefInput(curr)
            }
          }}
          style={{ padding: 0 }}
          placeholder={props.placeholder}
          className="home__searching--input home__searching--input-small"
          variant="borderless"
          value={value && getAirportCityName(value, { shownCode: true })}
          allowClear={!!value}
          onClear={handleClearAirport}
        />
        <FontAwesomeIcon icon={faChevronDown} className="home__searching--field-chevron" aria-hidden />
      </div>
    </div>
  ) : (
    <>
      <Row className="home__searching--input-label" align='middle' gutter={[5, 5]} wrap={false}>
        <Col>
          <Text>
            {props.icon}
          </Text>
        </Col>
        <Col>
          <Text>{props.label}</Text>
        </Col>
      </Row>
      <Row style={isSmall ? { marginTop: 0, height: '2.1rem' } : { marginBlock: '0.5rem' }}>
        <Col span={24} style={isSmall ? { height: '2.1rem' } : {}}>
          <InputSearch
            ref={(curr) => {
              refInputSearch.current = curr
              if (getRefInput) {
                getRefInput(curr)
              }
            }}

            style={isSmall ? { padding: 0 } : { paddingLeft: 0 }}
            placeholder={props.placeholder}
            className={`home__searching--input${isSmall ? ' home__searching--input-small' : ''}`}
            variant='borderless'
            value={value && getAirportCityName(value, { shownCode: true })}
            allowClear={!!value}
            onClear={handleClearAirport}
          />
        </Col>
      </Row>
      {size === 'default' && (
        <Row>
          <Text ellipsis={{ tooltip: true }} type='secondary' style={value ? {} : { visibility: 'hidden', opacity: 0 }}>
            {`${getTranslatedText(value?.nameVi, value?.nameEn)}`}
          </Text>
        </Row>
      )}
    </>
  )

  if (!onlyContent) {
    contentInput = (
      <Card
        ref={ref}
        size='small'
        className="home__searching--placement"
        onClick={() => openPopover()}
      >
        {contentInput}
      </Card>
    )
  }

  const popup = (
    <Popover
      open={open}
      onOpenChange={(p) => {
        if (clearingRef.current) {
          setOpen(false)
          return
        }
        if (p) {
          closeAllFlightSearchOverlays()
          setOpen(true)
        } else {
          setOpen(false)
        }
      }}
      destroyOnHidden
      trigger={[]}
      arrow={false}
      placement={popoverPlacement}
      styles={{
        content: {
          padding: '0',
          maxHeight: "50rem",
          overflow: 'auto',
          boxSizing: 'border-box',
          ...(popoverWidthPx != null
            ? { width: popoverWidthPx, maxWidth: popoverWidthPx }
            : { width: '43rem' }),
        }
      }}
      content={(
        <Row
          ref={refContent}
        >
          <Col span={24}>
            <RecentlySearch
              onChoose={() => {
                setOpen(false)
              }}
            />
            {hasRecentVisible && !searching && (<Divider style={{ marginBlock: '1.2rem' }} />)}
            {content}
          </Col>
        </Row>
      )}
    >
      {contentInput}
    </Popover>
  )

  return (
    <SelectPlacementContext.Provider
      value={{
        onChange: handleChange,
        chooseCurrentLy,
        onSearch,
        searching,
        size,
        value,
        open: openPopover
      }}
    >
      {!isPopup ? content : popup}
    </SelectPlacementContext.Provider>
  )
}


SelectPlacement.displayName = 'SelectPlacementProps'

export default memo(SelectPlacement)
